import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, Lock } from 'lucide-react';
import { GTM } from '@/utils/gtm';
import GoogleLoginButton from '@/components/GoogleLoginButton';
import { clearHasKennelCache } from '@/hooks/useHasKennel';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Wsparcie dla `?redirect=/wizard/miot` (z EditBreederPage banner „Mam już konto")
  const redirectTarget = searchParams.get('redirect');
  // Wsparcie dla `?flow=miot` (continuation post-email-verify) — użyte gdy redirect brak
  const flow = searchParams.get('flow');
  const flowRedirect = flow === 'miot' ? '/wizard/miot'
                     : flow === 'reproduktor' ? '/wizard/reproduktor'
                     : null;

  // Show error from Google auth if present
  useEffect(() => {
    if (location.state?.error) {
      toast.error(location.state.error);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      GTM.login('email');
      clearHasKennelCache();
      toast.success(t('auth.loginSuccess'));

      // Priorytet 1: explicit redirect z URL-a (np. „Mam już konto" w EditBreederPage)
      const finalDest = redirectTarget || flowRedirect;
      if (finalDest) {
        navigate(finalDest);
        return;
      }

      // P1.5 — Flow po logowaniu: ZAWSZE kierujemy na /panel.
      // Decyzję „Start Mode vs Home" przejmuje DashboardHomeV2 na podstawie REALNEGO
      // stanu z backendu (user.profile_type + faktyczny profil hodowli/firmy), a NIE
      // localStorage. Dzięki temu powracający użytkownik logujący się na nowym telefonie
      // (pusty localStorage) trafia od razu do swojego Centrum dowodzenia, a wybór profilu
      // pokaże się wyłącznie nowym użytkownikom bez żadnego workspace.
      navigate('/panel');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'string') {
        toast.error(detail);
      } else if (err.response?.status === 401) {
        toast.error(t('auth.invalidCredentials', 'Nieprawidłowy email lub hasło'));
      } else {
        toast.error(t('auth.loginError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060610] flex items-center justify-center px-6 relative overflow-hidden">
      {/* Grid + Glow Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-950/20 via-[#060610] to-[#060610]" />
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-orange-600/[0.08] rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/3 w-[300px] h-[300px] bg-orange-500/[0.06] rounded-full blur-[100px]" />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(249,115,22,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,.15) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img src="/logo.svg" alt="ApisAI" className="h-10" />
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">{t('auth.loginTitle')}</h1>
          <p className="text-zinc-400">{t('auth.loginSubtitle')}</p>
        </div>

        {/* Glass Card */}
        <div className="backdrop-blur-xl bg-[#0c0c1a]/80 border border-orange-500/15 rounded-2xl p-8 shadow-2xl shadow-orange-500/5">
          {/* Google Login */}
          <GoogleLoginButton className="mb-6" />
          
          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-zinc-500 text-sm">{t('common.or')}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300 text-sm">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  className="pl-11 bg-white/5 border-white/10 focus:border-orange-500/50 focus:bg-white/10 rounded-xl h-12 text-white placeholder:text-zinc-500 transition-all"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-gray-300 text-sm">{t('auth.password')}</Label>
                <Link to="/reset-hasla" className="text-sm text-orange-400 hover:text-orange-300 transition-colors">
                  {t('auth.forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pl-11 bg-white/5 border-white/10 focus:border-orange-500/50 focus:bg-white/10 rounded-xl h-12 text-white placeholder:text-zinc-500 transition-all"
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-400 hover:shadow-lg hover:shadow-orange-500/30 rounded-xl h-12 text-base font-bold transition-all"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('auth.login')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-400">
              {t('auth.noAccount')}{' '}
              <Link to="/rejestracja" className="text-orange-400 hover:text-orange-300 font-medium transition-colors">
                {t('auth.register')}
              </Link>
            </p>
            <p className="mt-3 text-sm text-zinc-500">
              Chcesz najpierw zobaczyć panel od środka?{' '}
              <Link to="/demo" className="text-orange-400 hover:text-orange-300 font-medium transition-colors" data-testid="login-demo-link">
                Wypróbuj demo
              </Link>
            </p>
          </div>
        </div>
        
        {/* Back to home link */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-zinc-500 hover:text-gray-300 text-sm transition-colors">
            ← {t('common.backToHome')}
          </Link>
        </div>

        {/* WhatsApp Onboarding CTA */}
        <div className="mt-5 text-center">
          <a
            href="https://wa.me/48507334540?text=Cze%C5%9B%C4%87!%20Chc%C4%99%20za%C5%82o%C5%BCy%C4%87%20konto%20w%20ApisAI.%20Mo%C5%BCecie%20mi%20pom%C3%B3c%3F"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#25D366]/10 border border-[#25D366]/20 hover:bg-[#25D366]/20 transition-all text-[#25D366] text-sm font-medium"
            data-testid="whatsapp-onboarding-btn"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Potrzebujesz pomocy? Napisz na WhatsApp
          </a>
        </div>
      </motion.div>
    </div>
  );
}
